import type { Route } from './+types/faq';
import type { FaqItem } from '@/components/sections/faq-section';
import { CtaBanner } from '@/components/sections/cta-banner';
import { FaqSection } from '@/components/sections/faq-section';
import { HeroSection } from '@/components/sections/hero-section';
import { buildFaqPageSchema, inheritRootLdJson, ldJsonMeta } from '@/utils/schema';

const ABOUT_FAQS: Array<FaqItem> = [
  {
    id: 'where-based',
    question: 'Where are you based and where do you operate?',
    answer:
      "We're based in Kingaroy and work across the South Burnett region of Queensland. While our focus is firmly local, our perspective is informed by years of experience across international property markets.",
  },
  {
    id: 'what-makes-different',
    question: 'What makes you different from other agencies?',
    answer:
      "We're not a high-volume agency. You'll always deal directly with Lucio — there's no being passed around a large team. We show up, communicate clearly and give honest, practical advice without the pressure.",
  },
];

const BUYING_SELLING_FAQS: Array<FaqItem> = [
  {
    id: 'where-to-start',
    question: "I'm thinking about selling. Where do I start?",
    answer:
      'The best first step is a confidential conversation. Lucio will walk you through current market conditions, give you an honest appraisal of your property and outline your options — with no obligation and no pressure.',
  },
  {
    id: 'rural-acreage',
    question: 'Do you handle rural and acreage properties?',
    answer:
      "Yes. We work across both residential and rural sales in the South Burnett, so whether you're selling a family home in town or a rural property, we can help.",
  },
  {
    id: 'buyers-agent',
    question: "What is a Buyer's Agent and do I need one?",
    answer:
      "A Buyer's Agent works exclusively for you as the purchaser — not the seller. They help you find the right property, assess its value, negotiate the best price and navigate the process from search to settlement. If you're new to the area or simply want expert guidance on your side, it's worth a conversation.",
  },
  {
    id: 'time-to-sell',
    question: 'How long does it typically take to sell a property in the South Burnett?',
    answer:
      'Timeframes vary depending on price, property type and market conditions. Lucio will give you an honest, realistic expectation upfront — not a number designed just to win your listing.',
  },
];

const PROPERTY_MANAGEMENT_FAQS: Array<FaqItem> = [
  {
    id: 'offer-property-management',
    question: 'Do you offer property management services?',
    answer:
      'Yes, we offer property management for residential and rural properties. We take a hands-on approach to looking after your investment and keeping you informed.',
  },
  {
    id: 'switch-property-manager',
    question: 'I already have a property manager. Can I switch to Meet Real Estate?',
    answer:
      "Absolutely. Changing property managers is simpler than most people expect. Contact Lucio and he'll explain the process and make the transition as smooth as possible.",
  },
];

const WORKING_WITH_US_FAQS: Array<FaqItem> = [
  {
    id: 'get-in-touch',
    question: 'How do I get in touch?',
    answer:
      "You can call or text Lucio directly on 0493 035 628, email lucio@meetrealestate.com.au, or book a confidential conversation through the website. We'll get back to you promptly — because we turn up, when others don't.",
  },
  {
    id: 'confidential-enquiry',
    question: 'Is my initial enquiry confidential?',
    answer:
      "Yes. Whether you're exploring options or ready to make a move, your conversation with us stays private. There's no obligation and no hard sell.",
  },
  {
    id: 'interstate-overseas',
    question: 'Do you work with interstate or overseas buyers?',
    answer:
      "Yes. Lucio's international background means he's well placed to assist buyers relocating to the region or investing from afar. We're experienced at managing the process remotely and keeping you fully informed at every step.",
  },
];

const ALL_FAQS: Array<FaqItem> = [
  ...ABOUT_FAQS,
  ...BUYING_SELLING_FAQS,
  ...PROPERTY_MANAGEMENT_FAQS,
  ...WORKING_WITH_US_FAQS,
];

export function meta({ matches }: Route.MetaArgs) {
  return [
    ...inheritRootLdJson(matches),
    { title: 'FAQs | Meet Real Estate — Kingaroy & South Burnett' },
    {
      name: 'description',
      content:
        'Answers to common questions about buying, selling and property management with Meet Real Estate in Kingaroy and the South Burnett.',
    },
    ldJsonMeta(buildFaqPageSchema(ALL_FAQS)),
  ];
}

export default function Faq() {
  return (
    <main>
      <HeroSection
        eyebrow="FAQs"
        heading="Frequently Asked Questions"
        description="Answers to common questions about buying, selling and property management in Kingaroy and the South Burnett."
        primaryCta={{ label: 'Ask Us Directly', to: '#contact' }}
      />

      <FaqSection id="about" className="scroll-mt-20" heading="About Meet Real Estate" faqs={ABOUT_FAQS} />

      <FaqSection
        id="buying-selling"
        className="section--light scroll-mt-20"
        heading="Buying & Selling"
        faqs={BUYING_SELLING_FAQS}
      />

      <FaqSection
        id="property-management"
        className="section--navy-mid scroll-mt-20"
        heading="Property Management"
        faqs={PROPERTY_MANAGEMENT_FAQS}
      />

      <FaqSection
        id="working-with-us"
        className="section--light scroll-mt-20"
        heading="Working With Us"
        faqs={WORKING_WITH_US_FAQS}
      />

      <CtaBanner
        id="contact"
        className="scroll-mt-20"
        heading="Still have questions?"
        description="If you didn't find what you were looking for, get in touch directly."
        primaryCta={{ label: 'Request an appraisal', to: '/appraisal' }}
        secondaryCta={{ label: 'Call Lucio', to: 'tel:0493035628' }}
        contactName="Lucio Passos"
        contactTitle="Sales Agent, Meet Real Estate"
        contactLinks={[
          { label: 'Mobile', value: '0493 035 628', href: 'tel:0493035628' },
          { label: 'Office', value: '07 3355 7777', href: 'tel:0733557777' },
          { label: 'Email', value: 'lucio@meetrealestate.com.au', href: 'mailto:lucio@meetrealestate.com.au' },
          { label: 'Website', value: 'meetrealestate.com.au', href: 'https://meetrealestate.com.au' },
        ]}
      />
    </main>
  );
}
